import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbArrowLeft, TbChevronRight } from "react-icons/tb";
import { CardNetworkLogo } from "../../../../Components/Atoms/CardNetworkLogo";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { fetchBaasCardPrograms } from "../../../../Services/baasCardProgramsApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { BaasCardsListSkeleton } from "../components/BaasSkeleton";
import { BAAS_CARTOES_PATH } from "../constants";

const formatSettlement = (schedule) => {
  if (!schedule) return "—";
  if (schedule.frequency === "realtime") {
    return intl.get("baas.cartoes.programs.settlement.realtime");
  }
  const cutoff = schedule.cutoffTime
    ? intl.get("baas.cartoes.programs.settlement.dailyCutoff", { time: schedule.cutoffTime })
    : intl.get("baas.cartoes.programs.settlement.daily");
  return `${schedule.type} · ${cutoff}`;
};

export const BaasCartoesSettlementPage = () => {
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const [programs, setPrograms] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setPrograms([]);
        return;
      }

      setDataLoading(true);
      try {
        const next = await fetchBaasCardPrograms(selectedInstance);
        if (!cancelled) setPrograms(next);
      } catch (error) {
        if (!cancelled) {
          setPrograms([]);
          addToast({
            type: "error",
            title: formatBaasConsoleConflictMessage(
              error,
              intl.get("baas.cartoes.programs.empty"),
            ),
          });
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, selectedInstance]);

  if (isLoading || dataLoading) {
    return <BaasCardsListSkeleton count={3} />;
  }

  return (
    <div className="baas-processing min-w-0 space-y-5">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {intl.get("baas.nav.cartoes")}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.cartoes.comingSoon.settlementTitle")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.settlementPage.subtitle")}
          </p>
        </div>
        <Link to={BAAS_CARTOES_PATH} className="btn-outline w-auto shrink-0 self-start !text-xs">
          <TbArrowLeft className="h-4 w-4" />
          {intl.get("baas.cartoes.comingSoon.backToPrograms")}
        </Link>
      </header>

      {programs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {intl.get("baas.cartoes.programs.empty")}
          </p>
        </div>
      ) : (
        <section className="min-w-0 space-y-3">
          <p className="dashboard-section-label">
            {intl.get("baas.cartoes.programs.listSection", { count: programs.length })}
          </p>
          <div className="flex flex-col gap-3">
            {programs.map((program) => (
              <Link
                key={program.id}
                to={`${BAAS_CARTOES_PATH}/${program.id}`}
                className="link-no-zoom group flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3.5 transition hover:border-gray-300 hover:shadow-sm dark:border-brand-dark-border dark:bg-brand-dark-surface dark:hover:border-gray-600"
              >
                <div className="min-w-0 flex items-start gap-3">
                  <CardNetworkLogo network={program.productDesign?.network} size={22} />
                  <div className="min-w-0">
                    <p className="truncate text-base font-bold text-brand-dark dark:text-gray-100">
                      {program.name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {intl.get("baas.cartoes.programs.settlementLabel")}
                      {": "}
                      {formatSettlement(program.settlementSchedule)}
                    </p>
                  </div>
                </div>
                <TbChevronRight className="h-5 w-5 shrink-0 text-gray-300 transition group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-400" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
